import { useCallback, useEffect, useRef, useState } from 'react';
import { trackAiSearchChipRemoval } from '@pkgs/components/utils/tracking';
import { getIsAiPromptSearchDisabled } from '@pkgs/components/utils/aiSearchPromptUtils';
import { type FilterChip, removeAiSearchFilterChip } from '@pkgs/components/utils/aiSearchChipUtils';
import { useSearchModuleTracking } from '@pkgs/components/hooks/useSearchModuleTracking';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { usePromptStore } from '@pkgs/components/hooks/stores/search/usePromptStore';
import { usePlaceDetails } from '@pkgs/components/hooks/queries/usePlaceDetails';
import { useAiSearch } from '@pkgs/components/hooks/queries/useAiSearch';
import { SearchGradient } from '@pkgs/components/assets/icons/small/SearchGradient';
import { AiStarGradient } from '@pkgs/components/assets/icons/small/AiStarGradient';
import { useOnClickOutside } from '@ev-tech/ui-core-components/utils';
import { useModalState } from '@ev-tech/ui-core-components/Modal';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';

import { PreferencesDetailsModal } from '../PreferenceScores/PreferencesDetailsModal';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { AiPromptInput } from '../AiPromptInput/AiPromptInput';

import styles from './AiLocationFilter.module.css';

export const DesktopAiLocationFilter = () => {
  const { countryCode } = useAppContext();
  const { isPending } = useAiSearch();
  const { placeDetailsList } = usePlaceDetails();
  const ref = useRef<HTMLDivElement>(null);
  const promptValue = usePromptStore((state) => state.promptValue);
  const getStateSnapshot = useSearchStore((state) => state.getStateSnapshot);
  const applyState = useSearchStore((state) => state.applyState);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const preferencesInfoModalState = useModalState();
  const [temporaryPromptValue, setTemporaryPromptValue] = useState(promptValue);
  const containerRef = useRef<HTMLDivElement>(null);
  const { pageContext, shopContext } = useSearchModuleTracking();

  useEffect(() => {
    setTemporaryPromptValue(promptValue);
  }, [promptValue]);

  const handlePromptValueChange = useCallback(
    (content: string) => {
      setTemporaryPromptValue(content);
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    },
    [isDropdownOpen],
  );

  useOnClickOutside(containerRef, () => {
    if (preferencesInfoModalState.isOpen) {
      return;
    }

    setIsDropdownOpen(false);
  });

  useEffect(() => {
    if (!isDropdownOpen) {
      return;
    }

    const handleDocumentFocusIn = (event: FocusEvent) => {
      if (preferencesInfoModalState.isOpen) {
        return;
      }

      if (event.target instanceof Node && containerRef.current?.contains(event.target)) {
        return;
      }

      setIsDropdownOpen(false);
    };

    document.addEventListener('focusin', handleDocumentFocusIn);

    return () => {
      document.removeEventListener('focusin', handleDocumentFocusIn);
    };
  }, [isDropdownOpen, preferencesInfoModalState.isOpen]);

  const isAiPromptSearchDisabled = getIsAiPromptSearchDisabled(promptValue, temporaryPromptValue);

  const handleSearchButtonClick = useCallback(() => {
    if (isAiPromptSearchDisabled) {
      setIsDropdownOpen(!isDropdownOpen);
      if (!isDropdownOpen) {
        ref.current?.focus();
      }
    }
  }, [isAiPromptSearchDisabled, isDropdownOpen]);

  const handleChipDelete = useCallback(
    (chip: FilterChip) => {
      trackAiSearchChipRemoval({
        source: 'drawer',
        chipVariant: chip.variant,
        pageContext,
        shopContext,
      });

      const nextState = removeAiSearchFilterChip({
        state: getStateSnapshot(),
        chip,
        marketCountryCode: countryCode,
        placeDetails: placeDetailsList,
      });
      applyState({ state: nextState });
      setIsDropdownOpen(false);
    },
    [applyState, countryCode, getStateSnapshot, pageContext, placeDetailsList, shopContext],
  );

  return (
    <>
      <div className={styles.desktopContainerWrapper} ref={containerRef}>
        <div className={styles.desktopContainer}>
          <AiPromptInput
            externalRef={ref}
            isAiPromptSearchDisabled={isAiPromptSearchDisabled}
            isDropdownOpen={isDropdownOpen}
            onClickHandler={() => setIsDropdownOpen(true)}
            preferencesInfoModalState={preferencesInfoModalState}
            onChipDelete={handleChipDelete}
            onChangeHandler={handlePromptValueChange}
            closeDropdown={() => setIsDropdownOpen(false)}
            temporaryPromptValue={temporaryPromptValue}
            shouldShowClearButton={Boolean(temporaryPromptValue) && !isPending}
            startAdornment={
              <div className={styles.startAdornmentContainer}>
                <AiStarGradient />
              </div>
            }
            endAdornmentRightSide={
              isPending ? (
                <IconButton className={styles.loadingIconButton} icon={<LoadingSpinner variant="ai" />} shape="square" disabled />
              ) : (
                <IconButton
                  className={styles.iconButton}
                  type={isAiPromptSearchDisabled ? 'button' : 'submit'}
                  onClick={handleSearchButtonClick}
                  icon={<SearchGradient />}
                  variant="secondary"
                  shape="square"
                />
              )
            }
          />
        </div>
      </div>
      <PreferencesDetailsModal modalState={preferencesInfoModalState} />
    </>
  );
};
